import React, { createContext, useContext, useEffect, useState } from "react";
import { get , find, noop , cloneDeep , set} from "lodash";
import moment from "moment";

import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { useReferralsStore, ReferralsStore } from "@stores/referralsStore";
import { CASE_STATUS_REASON_NOTE, MEMBER_DETAILS_CARE_TEAM_ASSIGNMENT, USER_ACTION_CARD_CARE_TEAM_ASSIGNMENT, USER_ACTION_DESCRIPTION_NOTE_SAVE, USER_ACTION_LOCATION_WORK_STATUS_DONE, USER_ACTION_MODULE_MEMBER_DETAIL, USER_ACTION_TAB_CARE_MANAGEMENT } from "@utils/constants";
import { getClientName } from "@utils/helpers";
import { usePatientData } from "@utils/customHooks";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import { useMemberDetailsStore } from "@stores/memberDetailsStore";
import { mappingOfCMRole } from "./referrals";

export type UseReferralProps = {};

const caseStatusReasonTxt = CASE_STATUS_REASON_NOTE;
const actionModule = USER_ACTION_MODULE_MEMBER_DETAIL;
const actionTab = USER_ACTION_TAB_CARE_MANAGEMENT;
const actionCard = USER_ACTION_CARD_CARE_TEAM_ASSIGNMENT;
const actionLocation = USER_ACTION_LOCATION_WORK_STATUS_DONE;
const actionDescription = USER_ACTION_DESCRIPTION_NOTE_SAVE;

export type UseReferrals = (props: UseReferralProps) => {
  patientId: string;
  loading?: boolean;
  referrals: any;
  store?: ReferralsStore;
  refresh?: () => void;
  updateReferrals?: any;
  isReferralUpdating : number | null;
  showModal : any;
  setShowModal : any,
  updateProgramDecline : any,
  isDeclineStatusUpdating : boolean
};

export const useFetchReferrals = () => {
  console.log("useReferralsContext.useFetchReferrals")

  const {patientId} = usePatientData();

  const store = useReferralsStore((store) => store);
  const clientName = getClientName();
  const { setReferrals } = store;

  const fetchReferrals = React.useCallback((setLoader : any = noop) => {
    if(clientName !== 'Mycare') {
      setLoader(true)
      memberDetailsService.getReferrals(patientId).subscribe(
        (response: any) => {
          const data = get(response, "data.data.data", []);
          setReferrals(data);
          setLoader(false);
        },
        (err) => {
          console.log(err);
          setLoader(false);
        }
      );
    }
  }, [clientName, patientId, setReferrals]);

  return {fetchReferrals}
}

export const useUpdateAttribute = (tempValueOfRole: { current: string; }) => {
  const store = useReferralsStore((store) => store);
  const cMember = membersService?.getCurrentMember;
  const memberData = useMemberDetailsStore((state) => state.memberData);
  const setMemberData = useMemberDetailsStore((state) => state.setMemberData);
  const setPrimaryUserRole = useReferralsStore((state) => state.setPrimaryUserRole);
  const [showLoader, setShowLoader] = useState<any>(false);
  const setCurrentCmModalVisibility = useReferralsStore((state) => state.setCurrentCmModalVisibility);

  const updatePrimaryUser = () => {
    const updatedCmId = get(
      find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: tempValueOfRole?.current }), "roleLabelForReferral", null) }),
      "assigne_id",
      null
    );
    const updatedCmName = get(
      find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: tempValueOfRole?.current }), "roleLabelForReferral", null) }),
      "assignee",
      null
    );

    setShowLoader(true);
    if (cMember && cMember?.patient_id) {
      const patient_id = parseInt(cMember?.patient_id);
      const payLoad = {
        attrib_name: "update_current_cm",
        currentAssignedCM: memberData?.currentCareManagerId,
        updatedCM: updatedCmId,
        role: tempValueOfRole?.current,
        patient_id: patient_id,
      };
      memberDetailsService.updateSelectedAttribute(payLoad).subscribe({
        next: (response: any) => {
          let temp_memDetail = cloneDeep(memberData);
          temp_memDetail = {
            ...temp_memDetail,
            attribCareManagerAssignmentStatus: updatedCmName === temp_memDetail?.memberDetails?.currentCareManagerName ? "Y" : "N",
            attribCareManagerId: updatedCmId,
            attribCareManagerName: updatedCmName,
          };
          setMemberData(temp_memDetail);
          setCurrentCmModalVisibility(false);
          setShowLoader(false);
          setPrimaryUserRole(tempValueOfRole?.current);
        },
        error: (error) => {
          setShowLoader(false);
          console.log(error);
          setCurrentCmModalVisibility(false);
        },
      });
    }
  }

  return { updatePrimaryUser, showLoader };

}

const useUpdateStatus = ({caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription , successCallback = noop, errorCallback = noop} : any) => {
  console.log("useReferralsContext.useUpdateStatus")
  const {patientId} = usePatientData();

  const updateStatus = React.useCallback(() => {
    membersService.updateStatus(patientId, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully");
        successCallback()
      },
      error: (e) => {
        console.log("update status failed");
        errorCallback()
      },
    });
  }, [actionCard, actionDescription, actionLocation, actionModule, actionTab, caseStatusReasonTxt, errorCallback, patientId, successCallback]);

  return {updateStatus} 
}

export const useReferrals: UseReferrals = function (props) {
  console.log("useReferralsContext.useReferrals")

  const {patientId} = usePatientData();

  const [loading, setloading] = useState<boolean>(false);
  const [isReferralUpdating, setIsReferralUpdating] = useState<number | null>(null);

  const [isDeclineStatusUpdating , setIsDeclineStatusUpdating] = useState<boolean>(false);

  const assignedPrograms = useCareCoordinationStore((state) => state.assignedPrograms);
  const setAssignedPrograms = useCareCoordinationStore((state) => state.setAssignedPrograms);

  const [showModal , setShowModal] = useState({});

  const store = useReferralsStore((store) => store);

  const { referrals, setReferrals } = store;

  const {fetchReferrals} = useFetchReferrals();

  const {updateStatus } = useUpdateStatus({caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription});

  const refresh = React.useCallback(() => {
    setloading(true);
    fetchReferrals(setloading);
  }, [fetchReferrals]);

  useEffect(() => {
    console.log("useReferrals.useEffect");
    refresh();
  }, [refresh]);

  const updateReferrals = React.useCallback(
    ({ status, order, assignee , noteContent, callback = noop}) => {
      console.log("useReferralsContext.updateReferrals")
      setIsReferralUpdating(order);
      if(noteContent){
        membersService.addNotes([patientId], noteContent, MEMBER_DETAILS_CARE_TEAM_ASSIGNMENT).subscribe({
          next: () => {},
          error: () => {},
        });
        updateStatus();
      }
      memberDetailsService.updateReferrals({ patientId, status, assignee }).subscribe(
        (response: any) => {
          const updatedReferral : any = find(referrals?.editable , {order});
          if(updatedReferral) {
            if(status?.status_val){
              updatedReferral.status = status.status_val;
            }
             if(assignee?.assignee_val) {
              updatedReferral.assignee = assignee.assignee_val;
              updatedReferral.assigne_id = assignee.assignee_id;
             }
          }
          setReferrals(referrals)
          setIsReferralUpdating(null);
          callback();
        },
        (err) => {
          setIsReferralUpdating(null);
        }
      );
    },
    [patientId, referrals, setReferrals, updateStatus]
  );

  const updateProgramDecline = ({prgmId , isDeclined} : any) => {
    setIsDeclineStatusUpdating(true);
    membersService.updateProgramDecline({ patientId, prgmId, isDeclined }).subscribe({
      next: (v) => {
        setIsDeclineStatusUpdating(false);
        const user = JSON.parse(localStorage.getItem("_user") || '');
        const data = cloneDeep(assignedPrograms);
        const program = find(data , (p) => Number(p.id) === Number(prgmId));
        if(program) {
          set(program , 'isDeclined' , isDeclined === 'Y');
          set(program , 'declinedBy' , `${get(user ,'lastName', '')}, ${get(user ,'firstName', '')}`);
          set(program , 'declinedAt' , moment.utc().format());
        }
        setAssignedPrograms(data);
      },
      error: (e) => {
        setIsDeclineStatusUpdating(false);
      },
    });
  }

  return {
    patientId,
    loading,
    referrals,
    store,
    refresh,
    updateReferrals,
    isReferralUpdating,
    showModal,
    setShowModal,
    updateProgramDecline,
    isDeclineStatusUpdating
  };
};

export const ReferalsContext = createContext<ReturnType<UseReferrals>>({
  loading: false,
  patientId: "",
  referrals: {},
  isReferralUpdating : null,
  showModal : false,
  setShowModal : noop,
  updateProgramDecline : noop,
  isDeclineStatusUpdating : false
});

export const ReferralsProvider: React.FC<UseReferralProps> = function ({ children, ...props }) {
  const referralsValue = useReferrals(props);

  return <ReferalsContext.Provider value={referralsValue}>{children}</ReferalsContext.Provider>;
};

export const useReferralsContext = () => useContext(ReferalsContext);
